import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useCallback } from 'react';

/** What the reader was trying to do when the gate stopped them. */
export type DownloadGateAction = 'download' | 'requestAccess';

export interface DownloadGateIntent {
    action: DownloadGateAction;
    /** Direct file link. Absent for a request-access intent. */
    link?: string;
    /** File name for the success screen ("your file's ready to download"). */
    fileName?: string;
    /** Specs-tier downloads create a lead once access is granted. */
    isSpecsTier?: boolean;
    productID: number;
    professionalID: number;
    /** Product hero for the gate's brand pane — the item being downloaded. */
    productImage?: string;
}

/**
 * The download the reader asked for before the gate stopped them, persisted so
 * it survives a social round-trip's full reload the same way the enquiry draft
 * does. Session-scoped: an abandoned intent must not follow them into a later
 * visit and start a download they have forgotten asking for.
 */
export const DOWNLOAD_GATE_INTENT_KEY = 'download-gate-intent';
export const DOWNLOAD_GATE_OPEN_KEY = 'download-gate-open';

export const downloadGateIntentAtom =
    atomWithStorage<DownloadGateIntent | null>(
        DOWNLOAD_GATE_INTENT_KEY,
        null,
        createJSONStorage<DownloadGateIntent | null>(() => sessionStorage),
    );

export const downloadGateOpenAtom = atomWithStorage<boolean>(
    DOWNLOAD_GATE_OPEN_KEY,
    false,
    createJSONStorage<boolean>(() => sessionStorage),
);

/** Read-only access for surfaces that only need to know an intent is pending. */
export const useDownloadGateIntent = () => useAtomValue(downloadGateIntentAtom);

export interface UseDownloadGate {
    intent: DownloadGateIntent | null;
    isOpen: boolean;
    /** Stop the reader at the gate, remembering what they were after. */
    open: (intent: DownloadGateIntent) => void;
    /** Close and forget — the intent must not outlive a dismissal. */
    close: () => void;
}

export const useDownloadGate = (): UseDownloadGate => {
    const [intent, setIntent] = useAtom(downloadGateIntentAtom);
    const [isOpen, setIsOpen] = useAtom(downloadGateOpenAtom);

    const open = useCallback(
        (next: DownloadGateIntent) => {
            setIntent(next);
            setIsOpen(true);
        },
        [setIntent, setIsOpen],
    );

    const close = useCallback(() => {
        setIsOpen(false);
        setIntent(null);
    }, [setIntent, setIsOpen]);

    return { intent, isOpen, open, close };
};

/** Clears a pending intent without touching the modal (post-download cleanup). */
export const useClearDownloadGateIntent = () => {
    const setIntent = useSetAtom(downloadGateIntentAtom);
    return useCallback(() => setIntent(null), [setIntent]);
};
